import React, { useState } from "react"
import { StyledTextField } from "components/inputs/StyledTextField.tsx"
import {
    RuleType,
    TextFieldNumberOptions,
} from "layouts/charging-rules/interface/Rules.ts"
import { useChargingRulesContext } from "layouts/charging-rules/context/useChargingRulesContext.ts"
import ErrorIcon from '@mui/icons-material/Error';

interface MaxRechargeEnergyProps {
    ruleOptions: TextFieldNumberOptions
    language: string
    ruleType: RuleType
    min: number
    max: number
}

export const ChargingRulesTextFieldComponent: React.FC<
    MaxRechargeEnergyProps
> = ({ ruleOptions: ruleOptions, language, ruleType, min, max }) => {

    const [inputValue, setInputValue] = useState<string>(ruleOptions.value?.toString() ?? "");
    const [error, setError] = useState<string | null>(null)
    const {
        setErrorDuration,
        setErrorEnergy,
        dispatch
    } = useChargingRulesContext()

    const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setInputValue(newValue);

        if (newValue.trim() === "") {
            setError(language === "fr" ? "Ce champ est requis" : "This field is required");
            if (ruleType === RuleType.MaxRechargeDurationOptions) setErrorDuration(true);
            if (ruleType === RuleType.MaxRechargeEnergyOptions) setErrorEnergy(true);
            dispatch({
                type: "updateValue",
                ruleType,
                value: null
            });
            return;
        }

        const parsedValue = parseFloat(newValue);
        if (!isNaN(parsedValue)) {
            dispatch({
                type: "updateValue",
                ruleType,
                value: parsedValue
            });

            if (ruleType === RuleType.MaxRechargeDurationOptions) {
                if (parsedValue < 5 || parsedValue > 1440) {
                    setErrorDuration(true);
                    return setError(language === "fr"
                        ? "Veuillez sélectionner une durée entre 5 mins et 24h (1440mins)"
                        : "Please select a duration between 5 mins and 24h (1440 mins)"
                    );
                } else {
                    setErrorDuration(false);
                    setError(null);
                }
            } else if (ruleType === RuleType.MaxRechargeEnergyOptions) {
                if (parsedValue < 5 || parsedValue > 400) {
                    setErrorEnergy(true);
                    return setError(language === "fr"
                        ? "Veuillez sélectionner une énergie entre 5 kWh et 400 kWh"
                        : "Please select an energy between 5 kWh and 400 kWh"
                    );
                } else {
                    setErrorEnergy(false);
                    setError(null);
                }
            }
        }
    };
    const goodLabel = () => {
        switch (ruleType) {
            case RuleType.MaxRechargeEnergyOptions:
                return language === "fr" ? "Puissance en Kwh" : "Power in Kwh"
            case RuleType.MaxRechargeDurationOptions:
                return language === "fr"
                    ? "Durée en minutes"
                    : "Duration in minutes"
        }
    }

    return (
        <StyledTextField
            variant="filled"
            sx={{
                width: { xs: '100%',  lg: '50%', xl: '33%' },
                '& .MuiFilledInput-root:hover': {
                    backgroundColor: 'grey.100'
                },
                '& .MuiFilledInput-root': {
                    backgroundColor: 'grey.100',
                    '&:before, &:after, &:hover:not(.Mui-disabled):before': {
                        borderBottom: 'none'
                    },
                    '&.Mui-focused, &.Mui-filled': {
                        backgroundColor: 'grey.100'
                    },
                    '& .MuiInputAdornment-root': {
                        cursor: 'pointer',
                        '&:hover': {
                            color: 'error.main'
                        }
                    }},
                '& .MuiFormHelperText-root': {
                    backgroundColor: 'transparent',
                    marginLeft: 0,
                    marginRight: 0,
                    fontWeight: 500
                },
                '& input[type="search"]::-webkit-search-cancel-button': {
                    WebkitAppearance: 'none',
                    appearance: 'none',
                    height: '14px',
                    width: '14px',
                    background: `
                        linear-gradient(45deg, 
                            transparent calc(45% - 0.5px), 
                            #4D5358 45%, 
                            #4D5358 55%, 
                            transparent calc(55% + 0.5px)
                        ),
                        linear-gradient(-45deg, 
                            transparent calc(45% - 0.5px), 
                            #4D5358 45%, 
                            #4D5358 55%, 
                            transparent calc(55% + 0.5px)
                        )`,
                    backgroundSize: '100% 100%',
                    backgroundPosition: 'center',
                    borderRadius: '1px',
                    cursor: 'pointer',
                    opacity: 1,
                    marginRight: '4px',

                },
                // Firefox
                '& input[type="search"]::-moz-search-clear-button': {
                    height: '14px',
                    width: '14px',
                    background: `
                       linear-gradient(45deg, transparent 40%, #4D5358 40%, #4D5358 60%, transparent 60%),
                       linear-gradient(-45deg, transparent 40%, #4D5358 40%, #4D5358 60%, transparent 60%)`,
                    backgroundSize: '100% 100%',
                    backgroundPosition: 'center',
                    cursor: 'pointer',
                    opacity: 1,
                    marginRight: '4px'
                }
        }}
            type="search"
            label={goodLabel()}
            value={inputValue}
            onChange={handleValueChange}
            error={!!error}
            helperText={
            error ?
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-cy={`nav-charging-rules__${ruleType}--error`}>
                    <ErrorIcon fontSize="small" color="error" /> {error}
                </span>
                : null
        }
            slotProps={{
                input: {
                    inputProps: {
                        min,
                        max,
                    },
                },
            }}
            onKeyDown={(e) => {
                const allowedKeys = [
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                ]
                if (!/^\d$/.test(e.key) && !allowedKeys.includes(e.key)) {
                    e.preventDefault()
                }
            }}
            data-cy={`nav-charging-rules__${ruleType}--input`}
        />
    )
}
